import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import yaml from 'js-yaml';
import { getTaskStatus } from './api';
import Tooltip from './Tooltip';

const DRAFT_KEY = 'gongju_glossary_editor_draft_v1';
const SEED_KEY = 'gongju_glossary_editor_seed_v1';

const TERM_TYPES = ['person', 'org', 'place', 'tech'];
const TERM_MODES = ['prefer', 'strict'];

function labelTermType(t, value) {
  const v = safeString(value).trim();
  return t(`glossaryEditor.termTypes.${v}`, { defaultValue: v || '-' });
}

function labelMode(t, value) {
  const v = safeString(value).trim();
  return t(`glossaryEditor.modes.${v}`, { defaultValue: v || '-' });
}

function safeString(v) {
  return String(v == null ? '' : v);
}

function safeLower(v) {
  return safeString(v).toLowerCase();
}

function normalizeDoc(raw) {
  const obj = raw && typeof raw === 'object' ? raw : {};
  const version = Number(obj.version) || 1;
  const meta = obj.meta && typeof obj.meta === 'object' ? obj.meta : {};
  const entries = Array.isArray(obj.entries) ? obj.entries : [];
  return { version, meta, entries };
}

function inferTargetLangs(doc) {
  const set = new Set();
  const metaTargets = Array.isArray(doc?.meta?.target_langs) ? doc.meta.target_langs : [];
  for (const lang of metaTargets) {
    const s = safeString(lang).trim();
    if (s) set.add(s);
  }
  for (const e of Array.isArray(doc?.entries) ? doc.entries : []) {
    const targets = e?.targets && typeof e.targets === 'object' ? e.targets : null;
    if (!targets) continue;
    for (const k of Object.keys(targets)) {
      const s = safeString(k).trim();
      if (s) set.add(s);
    }
  }
  const list = Array.from(set);
  list.sort();
  return list.length ? list : ['zh-Hans'];
}

function tryParseYamlOrJson(text) {
  const raw = safeString(text).trim();
  if (!raw) return { doc: null, format: null, error: null };
  try {
    if (raw.startsWith('{') || raw.startsWith('[')) {
      const parsed = JSON.parse(raw);
      return { doc: normalizeDoc(parsed), format: 'json', error: null };
    }
  } catch (_) {
    // fall through to YAML
  }
  try {
    const parsed = yaml.load(raw);
    return { doc: normalizeDoc(parsed), format: 'yaml', error: null };
  } catch (e) {
    return { doc: null, format: null, error: e };
  }
}

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function fetchText(url) {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.text();
}

function sampleDoc() {
  return {
    version: 1,
    meta: {
      name: 'my-glossary',
      generated_by: 'user',
    },
    entries: [
      {
        source: 'MrBeast',
        term_type: 'person',
        mode: 'strict',
        aliases: ['Mr Beast'],
        note: 'YouTuber name',
        targets: {
          'zh-Hans': { text: '野兽先生' },
          'zh-Hant': { text: '野獸先生' },
          en: { text: 'MrBeast' },
        },
      },
      {
        source: 'bank',
        term_type: 'tech',
        mode: 'prefer',
        note: 'Multi-meaning example',
        targets: {
          'zh-Hans': {
            variants: [
              { text: '银行', note: 'Finance context', confidence: 0.6 },
              { text: '河岸', note: 'Geography context', confidence: 0.4 },
            ],
            default: '银行',
          },
        },
      },
    ],
  };
}

function ensureEntry(doc, idx) {
  const entries = Array.isArray(doc.entries) ? doc.entries.slice() : [];
  while (entries.length <= idx) entries.push({});
  return entries;
}

function ensureTargets(entry) {
  const targets = entry.targets && typeof entry.targets === 'object' ? { ...entry.targets } : {};
  return targets;
}

export default function GlossaryEditorPage({ tasks, onRefreshTasks }) {
  const { t } = useTranslation();

  const [doc, setDoc] = useState(() => normalizeDoc(sampleDoc()));
  const [selectedLang, setSelectedLang] = useState('zh-Hans');
  const [parseError, setParseError] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasteFormat, setPasteFormat] = useState('yaml'); // 'yaml' | 'json'
  const [pasteFocused, setPasteFocused] = useState(false);
  const [variantsModal, setVariantsModal] = useState(null); // { entryIndex, lang }
  const [noteModal, setNoteModal] = useState(null); // { entryIndex }

  const [taskId, setTaskId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('task') || params.get('task_id') || '';
    } catch (_) {
      return '';
    }
  });
  const [loadingTask, setLoadingTask] = useState(false);
  const [taskError, setTaskError] = useState('');
  const [glossaryOutputs, setGlossaryOutputs] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState(null);

  const fileInputRef = useRef(null);

  const targetLangs = useMemo(() => inferTargetLangs(doc), [doc]);

  const serializedDocText = useMemo(() => {
    try {
      if (pasteFormat === 'json') return JSON.stringify(doc, null, 2);
      return yaml.dump(doc, { noRefs: true, lineWidth: 120, sortKeys: false });
    } catch (_) {
      return '';
    }
  }, [doc, pasteFormat]);

  const selectOptions = useMemo(() => {
    const list = Array.isArray(tasks) ? tasks.slice().reverse() : [];
    return list;
  }, [tasks]);

  useEffect(() => {
    if (!selectedLang || !targetLangs.includes(selectedLang)) {
      setSelectedLang(targetLangs[0] || 'zh-Hans');
    }
  }, [targetLangs, selectedLang]);

  const loadFromUrl = useCallback(
    async (url) => {
      const src = safeString(url || '').trim();
      if (!src) return;
      setTaskError('');
      setParseError('');
      try {
        const text = await fetchText(src);
        const parsed = tryParseYamlOrJson(text);
        if (!parsed.doc) {
          setParseError(t('glossaryEditor.parseFailed'));
          return;
        }
        setDoc(parsed.doc);
        if (parsed.format) setPasteFormat(parsed.format);
        setPasteText(text);
      } catch (e) {
        console.error('Failed to fetch glossary:', e);
        setTaskError(t('glossaryEditor.loadGlossaryFailed'));
      }
    },
    [t]
  );

  const loadTaskById = useCallback(
    async (id) => {
      const tid = safeString(id || '').trim();
      if (!tid) return;
      setLoadingTask(true);
      setTaskError('');
      try {
        const resp = await getTaskStatus(tid);
        const data = resp?.data || null;
        const outs = Array.isArray(data?.outputs) ? data.outputs : [];
        const gloss = outs.filter((o) => safeLower(o?.kind) === 'glossary_candidates');
        setGlossaryOutputs(gloss);
        const preferred =
          gloss.find((o) => safeLower(o?.filename).endsWith('.json')) ||
          gloss.find((o) => safeLower(o?.filename).endsWith('.yaml') || safeLower(o?.filename).endsWith('.yml')) ||
          gloss.find((o) => safeLower(o?.filename).endsWith('.txt')) ||
          gloss[0] ||
          null;
        setSelectedOutput(preferred);
        if (preferred?.url) await loadFromUrl(preferred.url);
      } catch (e) {
        console.error('Failed to load task for glossary editor:', e);
        setGlossaryOutputs([]);
        setSelectedOutput(null);
        setTaskError(t('glossaryEditor.loadTaskFailed'));
      } finally {
        setLoadingTask(false);
      }
    },
    [loadFromUrl, t]
  );

  const loadTask = useCallback(async () => {
    await loadTaskById(taskId);
  }, [loadTaskById, taskId]);

  useEffect(() => {
    // Priority: ?src= (direct file) > ?task= (task outputs) > local seed/draft.
    try {
      const params = new URLSearchParams(window.location.search || '');
      const src = safeString(params.get('src') || '').trim();
      if (src) {
        loadFromUrl(src);
        return;
      }
      const initialTask = safeString(params.get('task') || params.get('task_id') || '').trim();
      if (initialTask) {
        loadTaskById(initialTask);
        return;
      }

      const seed = safeString(window.localStorage.getItem(SEED_KEY) || '').trim();
      if (seed) {
        const parsed = tryParseYamlOrJson(seed);
        window.localStorage.removeItem(SEED_KEY);
        if (parsed.doc) setDoc(parsed.doc);
        return;
      }
      const draft = safeString(window.localStorage.getItem(DRAFT_KEY) || '').trim();
      if (draft) {
        const parsed = tryParseYamlOrJson(draft);
        if (parsed.doc) setDoc(parsed.doc);
      }
    } catch (_) {
      // ignore storage failures
    }
  }, [loadFromUrl, loadTaskById]);

  // Keep URL ?task= in sync so users can share/bookmark glossary editor links.
  useEffect(() => {
    const id = safeString(taskId || '').trim();
    if (!id) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('task', id);
      window.history.replaceState({}, '', url.toString());
    } catch (_) {
      // no-op
    }
  }, [taskId]);

  const onImportFile = useCallback(async (file) => {
    if (!file) return;
    setParseError('');
    try {
      const text = await file.text();
      const parsed = tryParseYamlOrJson(text);
      if (!parsed.doc) {
        setParseError(t('glossaryEditor.parseFailed'));
        return;
      }
      setDoc(parsed.doc);
      if (parsed.format) setPasteFormat(parsed.format);
      setPasteText(text);
    } catch (e) {
      console.error('Failed to import glossary file:', e);
      setParseError(t('glossaryEditor.parseFailed'));
    }
  }, [t]);

  const onLoadFromText = useCallback(() => {
    setParseError('');
    const parsed = tryParseYamlOrJson(pasteText);
    if (!parsed.doc) {
      setParseError(t('glossaryEditor.parseFailed'));
      return;
    }
    setDoc(parsed.doc);
    if (parsed.format) setPasteFormat(parsed.format);
  }, [pasteText, t]);

  // Keep the raw text box in sync with table edits (but never overwrite while the user is typing in it).
  useEffect(() => {
    if (pasteFocused) return;
    if (!serializedDocText) return;
    setPasteText((prev) => (prev === serializedDocText ? prev : serializedDocText));
  }, [serializedDocText, pasteFocused]);

  const saveDraft = useCallback(() => {
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(doc));
    } catch (_) {
      // ignore
    }
  }, [doc]);

  const clearDraft = useCallback(() => {
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch (_) {
      // ignore
    }
  }, []);

  const downloadYaml = useCallback(() => {
    const name = safeString(doc?.meta?.name).trim() || 'glossary';
    const content = yaml.dump(doc, { noRefs: true, lineWidth: 120, sortKeys: false });
    downloadTextFile(`${name}.yaml`, content);
  }, [doc]);

  const downloadJson = useCallback(() => {
    const name = safeString(doc?.meta?.name).trim() || 'glossary';
    downloadTextFile(`${name}.json`, JSON.stringify(doc, null, 2));
  }, [doc]);

  const updateEntryField = useCallback((idx, key, value) => {
    setDoc((prev) => {
      const next = normalizeDoc(prev);
      const entries = ensureEntry(next, idx);
      const entry = { ...(entries[idx] || {}) };
      entry[key] = value;
      entries[idx] = entry;
      return { ...next, entries };
    });
  }, []);

  const updateEntryTargetsText = useCallback((idx, lang, value) => {
    setDoc((prev) => {
      const next = normalizeDoc(prev);
      const entries = ensureEntry(next, idx);
      const entry = { ...(entries[idx] || {}) };
      const targets = ensureTargets(entry);
      const existing = targets[lang] && typeof targets[lang] === 'object' ? { ...targets[lang] } : {};
      // Do not overwrite variants in the simple table editor.
      if (Array.isArray(existing.variants) && existing.variants.length > 0) return next;
      const text = safeString(value);
      if (text.trim()) existing.text = text;
      else delete existing.text;
      targets[lang] = existing;
      entry.targets = targets;
      entries[idx] = entry;
      return { ...next, entries };
    });
  }, []);

  const addEntry = useCallback(() => {
    setDoc((prev) => {
      const next = normalizeDoc(prev);
      const entries = Array.isArray(next.entries) ? next.entries.slice() : [];
      entries.push({
        source: '',
        term_type: 'tech',
        mode: 'prefer',
        targets: { [selectedLang]: { text: '' } },
      });
      return { ...next, entries };
    });
  }, [selectedLang]);

  const deleteEntry = useCallback((idx) => {
    setDoc((prev) => {
      const next = normalizeDoc(prev);
      const entries = Array.isArray(next.entries) ? next.entries.slice() : [];
      entries.splice(idx, 1);
      return { ...next, entries };
    });
  }, []);

  const openVariants = useCallback((idx) => {
    setVariantsModal({ entryIndex: idx, lang: selectedLang });
  }, [selectedLang]);

  const closeVariants = useCallback(() => setVariantsModal(null), []);
  const closeNote = useCallback(() => setNoteModal(null), []);

  const variantsModel = useMemo(() => {
    if (!variantsModal) return null;
    const idx = variantsModal.entryIndex;
    const lang = variantsModal.lang;
    const entry = Array.isArray(doc?.entries) ? doc.entries[idx] : null;
    const targets = entry?.targets && typeof entry.targets === 'object' ? entry.targets : {};
    const target = targets?.[lang] && typeof targets[lang] === 'object' ? targets[lang] : {};
    const variants = Array.isArray(target.variants) ? target.variants : [];
    const def = safeString(target.default || '');
    return { idx, lang, variants, def };
  }, [variantsModal, doc]);

  const noteModel = useMemo(() => {
    if (!noteModal) return null;
    const idx = noteModal.entryIndex;
    const entry = Array.isArray(doc?.entries) ? doc.entries[idx] : null;
    const evidence = entry?.evidence && typeof entry.evidence === 'object' ? entry.evidence : null;
    const examples = Array.isArray(evidence?.examples) ? evidence.examples : [];
    return {
      idx,
      source: safeString(entry?.source || ''),
      note: safeString(entry?.note || ''),
      examples,
    };
  }, [noteModal, doc]);

  const updateVariants = useCallback((idx, lang, nextVariants, nextDefault) => {
    setDoc((prev) => {
      const next = normalizeDoc(prev);
      const entries = ensureEntry(next, idx);
      const entry = { ...(entries[idx] || {}) };
      const targets = ensureTargets(entry);
      const target = targets[lang] && typeof targets[lang] === 'object' ? { ...targets[lang] } : {};
      const clean = Array.isArray(nextVariants) ? nextVariants : [];
      target.variants = clean;
      const def = safeString(nextDefault || '').trim();
      if (def) target.default = def;
      else delete target.default;
      // In variants mode, remove simple `text` to avoid ambiguity.
      delete target.text;
      targets[lang] = target;
      entry.targets = targets;
      entries[idx] = entry;
      return { ...next, entries };
    });
  }, []);

  return (
    <div className="GlossaryEditorPage">
      <div className="Row Row--between Row--center GlossaryTop">
        <div>
          <div className="CardTitle">{t('glossaryEditor.title')}</div>
          <div className="SmallNote">{t('glossaryEditor.hint')}</div>
        </div>
        <div className="Row Row--center">
          <a className="Btn Btn--ghost Btn--small" href="/subtranslator/">
            {t('glossaryEditor.back')}
          </a>
        </div>
      </div>

      <div className="Card GlossaryTaskControls">
        <div className="Row Row--wrap Row--center">
          <label className="Field Field--inline GlossaryField">
            <span className="FieldLabel">{t('glossaryEditor.taskId')}</span>
            <input
              type="text"
              value={safeString(taskId || '')}
              onChange={(e) => setTaskId(e.target.value)}
              placeholder={t('glossaryEditor.taskIdPlaceholder')}
              autoComplete="off"
            />
          </label>
          <button
            className="Btn Btn--primary Btn--small"
            type="button"
            disabled={loadingTask || !safeString(taskId || '').trim()}
            onClick={loadTask}
          >
            {loadingTask ? t('glossaryEditor.loading') : t('glossaryEditor.load')}
          </button>
          <button
            className="Btn Btn--ghost Btn--small"
            type="button"
            onClick={() => (typeof onRefreshTasks === 'function' ? onRefreshTasks() : null)}
            disabled={typeof onRefreshTasks !== 'function'}
          >
            {t('button.refresh')}
          </button>
        </div>

        <label className="Field">
          <div className="FieldLabel">{t('glossaryEditor.selectFromTasks')}</div>
          <select value={safeString(taskId || '')} onChange={(e) => setTaskId(e.target.value)}>
            <option value="">{t('glossaryEditor.selectPlaceholder')}</option>
            {selectOptions.map((it) => (
              <option key={it.id} value={it.id}>
                {it.input_name || it.input_url || it.id}
              </option>
            ))}
          </select>
        </label>

        {Array.isArray(glossaryOutputs) && glossaryOutputs.length > 0 && (
          <label className="Field">
            <div className="FieldLabel">{t('glossaryEditor.glossaryFile')}</div>
            <select
              value={selectedOutput?.filename || ''}
              onChange={(e) => {
                const name = e.target.value;
                const found = glossaryOutputs.find((o) => o?.filename === name) || null;
                setSelectedOutput(found);
                if (found?.url) loadFromUrl(found.url);
              }}
            >
              {glossaryOutputs.map((o) => (
                <option key={o.filename} value={o.filename}>
                  {o.label || o.filename}
                </option>
              ))}
            </select>
            {selectedOutput?.url && (
              <div className="SmallNote">
                <a className="Chip" href={selectedOutput.url} target="_blank" rel="noopener noreferrer">
                  {t('glossaryEditor.openRaw')}
                </a>
              </div>
            )}
          </label>
        )}

        {taskError && <div className="ReviewError">{taskError}</div>}
      </div>

      <div className="Card GlossaryControls">
        <div className="Row Row--wrap Row--center">
          <button
            className="Btn Btn--ghost Btn--small"
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {t('glossaryEditor.importFile')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".yaml,.yml,.json,.txt"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) onImportFile(file);
              e.target.value = '';
            }}
          />

          <button className="Btn Btn--ghost Btn--small" type="button" onClick={() => setDoc(normalizeDoc(sampleDoc()))}>
            {t('glossaryEditor.newSample')}
          </button>

          <button className="Btn Btn--ghost Btn--small" type="button" onClick={saveDraft}>
            {t('glossaryEditor.saveDraft')}
          </button>
          <button className="Btn Btn--ghost Btn--small" type="button" onClick={clearDraft}>
            {t('glossaryEditor.clearDraft')}
          </button>

          <button className="Btn Btn--primary Btn--small" type="button" onClick={downloadYaml}>
            {t('glossaryEditor.downloadYaml')}
          </button>
          <button className="Btn Btn--primary Btn--small" type="button" onClick={downloadJson}>
            {t('glossaryEditor.downloadJson')}
          </button>
        </div>

        <div className="Row Row--wrap Row--center GlossaryMetaRow">
          <label className="Field Field--inline GlossaryField">
            <span className="FieldLabel">{t('glossaryEditor.metaName')}</span>
            <input
              type="text"
              value={safeString(doc?.meta?.name || '')}
              onChange={(e) => setDoc((prev) => ({ ...normalizeDoc(prev), meta: { ...(normalizeDoc(prev).meta || {}), name: e.target.value } }))}
              placeholder="my-glossary"
            />
          </label>

          <label className="Field Field--inline GlossaryField">
            <span className="FieldLabel">{t('glossaryEditor.targetLang')}</span>
            <select value={selectedLang} onChange={(e) => setSelectedLang(e.target.value)}>
              {targetLangs.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="Field">
          <div className="FieldLabel">{t('glossaryEditor.pasteLabel')}</div>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            onFocus={() => setPasteFocused(true)}
            onBlur={() => setPasteFocused(false)}
            rows={6}
            placeholder={t('glossaryEditor.pastePlaceholder')}
          />
          <div className="Row Row--wrap">
            <button className="Btn Btn--ghost Btn--small" type="button" onClick={onLoadFromText} disabled={!pasteText.trim()}>
              {t('glossaryEditor.loadText')}
            </button>
          </div>
        </label>

        {parseError && <div className="ReviewError">{parseError}</div>}
      </div>

      <div className="Card GlossaryTableCard">
        <details className="Details">
          <summary>{t('glossaryEditor.polysemyGuideTitle')}</summary>
          <div className="SmallNote">{t('glossaryEditor.polysemyGuideText')}</div>
          <pre className="CodeBlock">{t('glossaryEditor.polysemyExample')}</pre>
        </details>

        <div className="GlossaryTableWrap" role="region" aria-label={t('glossaryEditor.tableLabel')}>
          <table className="GlossaryTable">
            <thead>
              <tr>
                <th>{t('glossaryEditor.table.source')}</th>
                <th>
                  {t('glossaryEditor.table.type')}
                  <Tooltip text={t('glossaryEditor.help.type')} placement="bottom">
                    <span className="InfoIcon" aria-label={t('glossaryEditor.help.type')} tabIndex={0}>
                      ?
                    </span>
                  </Tooltip>
                </th>
                <th>
                  {t('glossaryEditor.table.mode')}
                  <Tooltip text={t('glossaryEditor.help.mode')} placement="bottom">
                    <span className="InfoIcon" aria-label={t('glossaryEditor.help.mode')} tabIndex={0}>
                      ?
                    </span>
                  </Tooltip>
                </th>
                <th>
                  {t('glossaryEditor.table.translation')}
                  <Tooltip text={t('glossaryEditor.help.translation')} placement="bottom">
                    <span className="InfoIcon" aria-label={t('glossaryEditor.help.translation')} tabIndex={0}>
                      ?
                    </span>
                  </Tooltip>
                </th>
                <th>
                  {t('glossaryEditor.table.aliases')}
                  <Tooltip text={t('glossaryEditor.help.aliases')} placement="bottom">
                    <span className="InfoIcon" aria-label={t('glossaryEditor.help.aliases')} tabIndex={0}>
                      ?
                    </span>
                  </Tooltip>
                </th>
                <th>{t('glossaryEditor.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(doc?.entries) ? doc.entries : []).map((entry, idx) => {
                const termType = TERM_TYPES.includes(entry?.term_type) ? entry.term_type : 'tech';
                const mode = TERM_MODES.includes(entry?.mode) ? entry.mode : 'prefer';
                const targets = entry?.targets && typeof entry.targets === 'object' ? entry.targets : {};
                const target = targets?.[selectedLang] && typeof targets[selectedLang] === 'object' ? targets[selectedLang] : {};
                const hasVariants = Array.isArray(target?.variants) && target.variants.length > 0;
                const aliases = Array.isArray(entry?.aliases) ? entry.aliases.join(', ') : safeString(entry?.aliases || '');
                const hasNote = Boolean(safeString(entry?.note || '').trim());
                const evidence = entry?.evidence && typeof entry.evidence === 'object' ? entry.evidence : null;
                const hasEvidence = Array.isArray(evidence?.examples) && evidence.examples.length > 0;

                return (
                  <tr key={`entry-${idx}`}>
                    <td>
                      <input
                        className="GlossaryCellInput"
                        type="text"
                        value={safeString(entry?.source || '')}
                        onChange={(e) => updateEntryField(idx, 'source', e.target.value)}
                      />
                    </td>
                    <td>
                      <select
                        className="GlossaryCellSelect"
                        value={termType}
                        onChange={(e) => updateEntryField(idx, 'term_type', e.target.value)}
                      >
                        {TERM_TYPES.map((tt) => (
                          <option key={tt} value={tt}>
                            {labelTermType(t, tt)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="GlossaryCellSelect"
                        value={mode}
                        onChange={(e) => updateEntryField(idx, 'mode', e.target.value)}
                      >
                        {TERM_MODES.map((m) => (
                          <option key={m} value={m}>
                            {labelMode(t, m)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {hasVariants ? (
                        <div className="Row Row--wrap Row--center GlossaryInline">
                          <span className="Badge Badge--warn">{t('glossaryEditor.variantsBadge', { count: target.variants.length })}</span>
                          <button className="Btn Btn--ghost Btn--small" type="button" onClick={() => openVariants(idx)}>
                            {t('glossaryEditor.editVariants')}
                          </button>
                        </div>
                      ) : (
                        <div className="Row Row--wrap Row--center GlossaryInline">
                          <input
                            className="GlossaryCellInput"
                            type="text"
                            value={safeString(target?.text || '')}
                            onChange={(e) => updateEntryTargetsText(idx, selectedLang, e.target.value)}
                            placeholder={t('glossaryEditor.translationPlaceholder', { lang: selectedLang })}
                          />
                          <Tooltip text={t('glossaryEditor.help.translation')}>
                            <button
                              className="Btn Btn--ghost Btn--small"
                              type="button"
                              onClick={() => {
                                const existing = safeString(target?.text || '').trim();
                                if (existing) {
                                  updateVariants(
                                    idx,
                                    selectedLang,
                                    [{ text: existing, note: '' }],
                                    existing
                                  );
                                } else {
                                  updateVariants(idx, selectedLang, [{ text: '', note: '' }], '');
                                }
                                openVariants(idx);
                              }}
                            >
                              {t('glossaryEditor.toVariants')}
                            </button>
                          </Tooltip>
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        className="GlossaryCellInput"
                        type="text"
                        value={aliases}
                        onChange={(e) => updateEntryField(idx, 'aliases', safeString(e.target.value).split(',').map((s) => s.trim()).filter(Boolean))}
                        placeholder={t('glossaryEditor.aliasesPlaceholder')}
                      />
                    </td>
                    <td>
                      <div className="GlossaryActions">
                        <Tooltip text={t('glossaryEditor.noteButtonHint')}>
                          <button
                            className="Btn Btn--ghost Btn--small"
                            type="button"
                            onClick={() => setNoteModal({ entryIndex: idx })}
                          >
                            {t('glossaryEditor.table.note')}
                            {(hasNote || hasEvidence) ? '*' : ''}
                          </button>
                        </Tooltip>
                        <button className="Btn Btn--ghost Btn--small" type="button" onClick={() => deleteEntry(idx)}>
                          {t('glossaryEditor.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      <div className="Row Row--wrap GlossaryTableActions">
        <button className="Btn Btn--primary" type="button" onClick={addEntry}>
          {t('glossaryEditor.addEntry')}
        </button>
      </div>
    </div>

      {variantsModel && (
        <div
          className="ModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('glossaryEditor.variantsModalTitle')}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeVariants();
          }}
        >
          <div className="Modal GlossaryModal">
            <div className="ModalHeader Row Row--between Row--center">
              <div>
                <div className="ModalTitle">{t('glossaryEditor.variantsModalTitle')}</div>
                <div className="SmallNote">{t('glossaryEditor.variantsModalHint', { lang: variantsModel.lang })}</div>
              </div>
              <div className="Row">
                <button className="Btn Btn--ghost Btn--small" type="button" onClick={closeVariants}>
                  {t('button.close')}
                </button>
              </div>
            </div>

            <div className="ModalBody">
              <label className="Field">
                <div className="FieldLabel">{t('glossaryEditor.defaultLabel')}</div>
                <input
                  type="text"
                  value={variantsModel.def}
                  onChange={(e) => updateVariants(variantsModel.idx, variantsModel.lang, variantsModel.variants, e.target.value)}
                  placeholder={t('glossaryEditor.defaultPlaceholder')}
                />
              </label>

              <div className="GlossaryVariantsList">
                {variantsModel.variants.map((v, i) => (
                  <div key={`var-${i}`} className="GlossaryVariantRow">
                    <input
                      className="GlossaryCellInput"
                      type="text"
                      value={safeString(v?.text || '')}
                      onChange={(e) => {
                        const next = variantsModel.variants.slice();
                        next[i] = { ...(next[i] || {}), text: e.target.value };
                        updateVariants(variantsModel.idx, variantsModel.lang, next, variantsModel.def);
                      }}
                      placeholder={t('glossaryEditor.variantText')}
                    />
                    <input
                      className="GlossaryCellInput"
                      type="text"
                      value={safeString(v?.note || '')}
                      onChange={(e) => {
                        const next = variantsModel.variants.slice();
                        next[i] = { ...(next[i] || {}), note: e.target.value };
                        updateVariants(variantsModel.idx, variantsModel.lang, next, variantsModel.def);
                      }}
                      placeholder={t('glossaryEditor.variantNote')}
                    />
                    <input
                      className="GlossaryCellInput GlossaryCellInput--small"
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={v?.confidence == null ? '' : String(v.confidence)}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        const next = variantsModel.variants.slice();
                        next[i] = { ...(next[i] || {}) };
                        if (Number.isFinite(n)) next[i].confidence = n;
                        else delete next[i].confidence;
                        updateVariants(variantsModel.idx, variantsModel.lang, next, variantsModel.def);
                      }}
                      placeholder={t('glossaryEditor.variantConfidence')}
                    />
                    <button
                      className="Btn Btn--ghost Btn--small"
                      type="button"
                      onClick={() => {
                        const next = variantsModel.variants.slice();
                        next.splice(i, 1);
                        updateVariants(variantsModel.idx, variantsModel.lang, next, variantsModel.def);
                      }}
                    >
                      {t('glossaryEditor.delete')}
                    </button>
                  </div>
                ))}
              </div>

              <div className="Row Row--wrap">
                <button
                  className="Btn Btn--primary"
                  type="button"
                  onClick={() => {
                    const next = variantsModel.variants.slice();
                    next.push({ text: '', note: '' });
                    updateVariants(variantsModel.idx, variantsModel.lang, next, variantsModel.def);
                  }}
                >
                  {t('glossaryEditor.addVariant')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {noteModel && (
        <div
          className="ModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('glossaryEditor.noteModalTitle')}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeNote();
          }}
        >
          <div className="Modal GlossaryModal">
            <div className="ModalHeader Row Row--between Row--center">
              <div>
                <div className="ModalTitle">{t('glossaryEditor.noteModalTitle')}</div>
                <div className="SmallNote">
                  {noteModel.source ? t('glossaryEditor.noteModalHint', { source: noteModel.source }) : t('common.na')}
                </div>
              </div>
              <div className="Row">
                <button className="Btn Btn--ghost Btn--small" type="button" onClick={closeNote}>
                  {t('button.close')}
                </button>
              </div>
            </div>

            <div className="ModalBody">
              <label className="Field">
                <div className="FieldLabel">{t('glossaryEditor.table.note')}</div>
                <textarea
                  value={noteModel.note}
                  onChange={(e) => updateEntryField(noteModel.idx, 'note', e.target.value)}
                  rows={6}
                  placeholder={t('glossaryEditor.notePlaceholder')}
                />
              </label>

              {Array.isArray(noteModel.examples) && noteModel.examples.length > 0 && (
                <div className="Section">
                  <div className="SectionTitle">{t('glossaryEditor.evidenceTitle')}</div>
                  <div className="GlossaryEvidence">
                    {noteModel.examples.map((ex, i) => (
                      <div key={`ex-${i}`} className="GlossaryEvidenceItem">
                        <div className="GlossaryEvidenceTime">{safeString(ex?.time_range || '')}</div>
                        <div className="GlossaryEvidenceText">{safeString(ex?.excerpt || '')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
